import AppBridgeHelper from "../helpers/AppBridgeHelper";
import * as ProductHelper from "../helpers/ProductHelper";
import * as ShopsHelper from "../helpers/ShopsHelper";
import ShopService from "./ShopService";
import { STORAGE_KEYS } from "../const";

const STORAGE_KEY = "label-scale";

function mergeLocalAndShopLabelScales(localMachines, shopLabelScales) {
  let nextId = localMachines.length > 0 ? Math.max(...localMachines.map((m) => Number(m.id) || 0)) + 1 : 1;
  const merged = [...localMachines];

  for (const scale of shopLabelScales) {
    const scaleMac = (scale.macId || scale.mac_id || "").trim().toUpperCase();
    if (!scaleMac) continue;

    const existingIndex = merged.findIndex((m) => (m.mac_id || "").trim().toUpperCase() === scaleMac);

    if (existingIndex !== -1) {
      merged[existingIndex] = {
        ...merged[existingIndex],
        machine_name: scale.machineName || scale.machine_name || merged[existingIndex].machine_name,
        machine_number: scale.machineNumber ?? scale.machine_number ?? merged[existingIndex].machine_number,
        mac_id: scaleMac,
      };
    } else {
      merged.push({
        id: nextId++,
        machine_name: scale.machineName || scale.machine_name || "",
        mac_id: scaleMac,
        machine_number: Number(scale.machineNumber ?? scale.machine_number) || 0,
        active: true,
      });
    }
  }
  return merged;
}

async function getMachines() {
  const data = localStorage.getItem(STORAGE_KEY);
  const localMachines = data ? JSON.parse(data) : [];

  try {
    const shopProfile = ShopsHelper.getCachedShopProfile() || (await ShopService.loadShopProfile());
    const shopLabelScales = shopProfile?.config?.label_scales || shopProfile?.label_scales || [];

    if (Array.isArray(shopLabelScales) && shopLabelScales.length > 0) {
      const allMachines = mergeLocalAndShopLabelScales(localMachines, shopLabelScales);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allMachines));
      return allMachines;
    }
  } catch (err) {
    console.error("Failed to load shop label scales:", err);
  }

  return localMachines;
}

async function updateMachine(id, data = {}) {
  const machines = await getMachines();
  const index = machines.findIndex((machine) => machine.id === id);

  if (index !== -1) {
    machines[index] = { ...machines[index], ...data };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(machines));
    await syncToNative();
  }
}

async function addMachine(data = {}) {
  const machines = await getMachines();
  if (!data.id) {
    const newId = machines.length > 0 ? Math.max(...machines.map((m) => m.id)) + 1 : 1;
    data.id = newId;
  }

  machines.push(data);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(machines));
  await syncToNative();
}

async function deactivateMachine(id, data = {}) {
  await updateMachine(id, data);
}

async function deleteMachine(id) {
  let machines = await getMachines();
  const machineToDelete = machines.find((machine) => machine.id === id);
  machines = machines.filter((machine) => machine.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(machines));

  if (machineToDelete?.mac_id) {
    await ShopsHelper.removeMachineFromShopProfileConfig(machineToDelete.mac_id);
  }

  await syncToNative();
}

async function syncToNative() {
  try {
    const bridge = AppBridgeHelper.getBridge();
    if (bridge && bridge.syncScaleData) {
      const machines = await getMachines();
      const products = await ProductHelper.getSyncEnabledProductsFromDB();
      const shopProfile = ShopsHelper.getCachedShopProfile() || {};

      const taxPrefStr = localStorage.getItem(STORAGE_KEYS.TAX_PREFERENCE) || "Including Tax";
      let taxPreference = 2; // Including
      if (taxPrefStr === "Excluding Tax") taxPreference = 1;
      if (taxPrefStr === "No Tax") taxPreference = 0;

      await bridge.syncScaleData(
        JSON.stringify(machines),
        JSON.stringify(products),
        JSON.stringify(shopProfile),
        taxPreference,
      );
    }
  } catch (error) {
    console.error("Failed to sync scale data to native:", error);
  }
}

async function updateProductsToOnlineMachines(item = null) {
  try {
    const machines = await getMachines();
    if (!machines || machines.length === 0) return;

    const bridge = AppBridgeHelper.getBridge();
    if (!bridge) return;

    if (!item) {
      await syncToNative();
    } else if (!item.sync_enabled) {
      return;
    }

    const itemJson = item ? JSON.stringify(item) : "";
    if (bridge.updateProductsToOnlineMachines) {
      await bridge.updateProductsToOnlineMachines(itemJson);
    } else if (bridge.syncProductsToMachine) {
      for (const machine of machines) {
        if (machine.mac_id) {
          await bridge.syncProductsToMachine(machine.mac_id, itemJson);
        }
      }
    }
  } catch (error) {
    console.error("Failed to update products to online scale machines:", error);
  }
}

const machineService = {
  getMachines,
  updateMachine,
  addMachine,
  deactivateMachine,
  deleteMachine,
  syncToNative,
  updateProductsToOnlineMachines,
};

export default machineService;
